/**
 * Web Audio API录音工具
 * 专为iPad设备提供的替代语音录制解决方案
 */

/**
 * 创建Web Audio API录音器
 * @param {Function} onDataAvailable 当有新的音频数据可用时的回调
 * @param {Function} onComplete 录音完成时的回调，参数为完整的音频数据
 * @param {Object} options 配置选项
 * @returns {Object} 录音控制对象
 */
export function createWebAudioRecorder(onDataAvailable, onComplete, options = {}) {
  // 默认配置
  const config = {
    sampleRate: 16000, // 输出采样率
    bufferSize: 4096,  // 缓冲区大小
    numberOfChannels: 1, // 单声道
    ...options
  };
  
  let audioContext = null;
  let mediaStream = null;
  let recorder = null;
  let audioChunks = [];
  let isRecording = false;
  
  /**
   * 初始化录音器
   * @returns {Promise} 初始化完成的Promise
   */
  const init = async () => {
    try {
      // 创建音频上下文
      audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: config.sampleRate
      });
      
      // 请求麦克风权限
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // 创建音频源
      const source = audioContext.createMediaStreamSource(mediaStream);
      
      // 创建脚本处理器节点
      const processorNode = audioContext.createScriptProcessor(
        config.bufferSize,
        config.numberOfChannels,
        config.numberOfChannels
      );
      
      // 处理音频数据
      processorNode.onaudioprocess = (e) => {
        if (!isRecording) return;
        
        const audioData = e.inputBuffer.getChannelData(0);
        const dataArray = new Float32Array(audioData);
        audioChunks.push(dataArray);
        
        if (onDataAvailable) {
          onDataAvailable(dataArray);
        }
      };
      
      // 连接节点
      source.connect(processorNode);
      processorNode.connect(audioContext.destination);
      
      // 保存处理器节点引用
      recorder = processorNode;
      
      return true;
    } catch (error) {
      console.error('初始化Web Audio录音器失败:', error);
      return false;
    }
  };
  
  /**
   * 开始录音
   * @returns {Promise} 开始录音的Promise
   */
  const start = async () => {
    if (isRecording) return false;
    
    // 如果尚未初始化，先初始化
    if (!recorder) {
      const initSuccess = await init();
      if (!initSuccess) return false;
    }
    
    // 清空之前的录音数据
    audioChunks = [];
    isRecording = true;
    
    // 如果音频上下文被暂停，恢复它
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    
    return true;
  };
  
  /**
   * 停止录音
   * @returns {Promise} 包含录音结果的Promise
   */
  const stop = async () => {
    if (!isRecording) return null;
    
    isRecording = false;
    
    // 合并所有音频块
    const recordedLength = audioChunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const mergedAudio = new Float32Array(recordedLength);
    
    let offset = 0;
    for (const chunk of audioChunks) {
      mergedAudio.set(chunk, offset);
      offset += chunk.length;
    }
    
    // 创建WAV格式数据
    const wavData = encodeWAV(mergedAudio, config.sampleRate, config.numberOfChannels);
    const audioBlob = new Blob([wavData], { type: 'audio/wav' });
    
    // 停止媒体流
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
    }
    
    // 调用完成回调
    if (onComplete) {
      onComplete(audioBlob);
    }
    
    return audioBlob;
  };
  
  /**
   * 将Float32Array数据编码为WAV格式
   * @param {Float32Array} samples 音频样本数据
   * @param {number} sampleRate 采样率
   * @param {number} numChannels 声道数
   * @returns {DataView} WAV格式的数据
   */
  const encodeWAV = (samples, sampleRate, numChannels) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    // RIFF标识
    writeString(view, 0, 'RIFF');
    // 文件长度
    view.setUint32(4, 36 + samples.length * 2, true);
    // WAVE标识
    writeString(view, 8, 'WAVE');
    // fmt子块标识
    writeString(view, 12, 'fmt ');
    // 子块长度
    view.setUint32(16, 16, true);
    // 音频格式（PCM = 1）
    view.setUint16(20, 1, true);
    // 声道数
    view.setUint16(22, numChannels, true);
    // 采样率
    view.setUint32(24, sampleRate, true);
    // 字节率
    view.setUint32(28, sampleRate * numChannels * 2, true);
    // 块对齐
    view.setUint16(32, numChannels * 2, true);
    // 每个样本的位数
    view.setUint16(34, 16, true);
    // data子块标识
    writeString(view, 36, 'data');
    // 数据长度
    view.setUint32(40, samples.length * 2, true);
    
    // 写入PCM样本
    floatTo16BitPCM(view, 44, samples);
    
    return view;
  };
  
  /**
   * 将字符串写入DataView
   * @param {DataView} view DataView对象
   * @param {number} offset 偏移量
   * @param {string} string 要写入的字符串
   */
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  /**
   * 将Float32Array转换为16位PCM
   * @param {DataView} view DataView对象
   * @param {number} offset 偏移量
   * @param {Float32Array} input 输入数据
   */
  const floatTo16BitPCM = (view, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };
  
  /**
   * 将录音数据转换为base64字符串
   * @param {Blob} blob 音频Blob对象
   * @returns {Promise<string>} base64编码的音频数据
   */
  const toBase64 = async (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  // 返回控制对象
  return {
    init,
    start,
    stop,
    toBase64,
    isRecording: () => isRecording
  };
}

/**
 * 将音频Blob转换为AudioBuffer
 * @param {Blob} audioBlob 音频Blob对象
 * @returns {Promise<AudioBuffer>} 解码后的AudioBuffer
 */
export async function blobToAudioBuffer(audioBlob) {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await audioBlob.arrayBuffer();
  return await audioContext.decodeAudioData(arrayBuffer);
}

/**
 * 将AudioBuffer转换为语音识别API可用的格式
 * @param {AudioBuffer} audioBuffer 音频缓冲区
 * @returns {Blob} 转换后的音频Blob
 */
export function audioBufferToWav(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length;
  
  // 获取音频数据
  const channelData = [];
  for (let i = 0; i < numChannels; i++) {
    channelData.push(audioBuffer.getChannelData(i));
  }
  
  // 创建WAV文件
  const buffer = new ArrayBuffer(44 + length * numChannels * 2);
  const view = new DataView(buffer);
  
  // 写入WAV头
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length * numChannels * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM格式
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length * numChannels * 2, true);
  
  // 写入音频数据
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = channelData[channel][i];
      // 将浮点数转换为16位整数
      const value = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, value < 0 ? value * 0x8000 : value * 0x7FFF, true);
      offset += 2;
    }
  }
  
  return new Blob([buffer], { type: 'audio/wav' });
  
  // 辅助函数：将字符串写入DataView
  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
} 